import { Trans, useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { useTheme } from 'styled-components';

import { WarningColorIcon } from '@treams/treams-icons';
import { Button, Modal, Paragraph, Popover } from '@treams/treams-ui';

interface IExternalRecipient {
	name: string;
	email: string;
}

interface IProps {
	externals: IExternalRecipient[];
	internalCount: number;
	isSubmitting?: boolean;
	onAdjust: () => void;
	onConfirm: () => void;
}

const RecipientCard = styled(Box)`
	background: ${(props) => props.theme.tokens.surface.white};
	border: 1px solid ${(props) => props.theme.tokens.border.subtle};
	border-radius: ${(props) => props.theme.tokens.radius.md}px;
`;

const EmailHighlight = styled.span`
	background: ${(props) => props.theme.tokens.brand.background};
	border-radius: ${(props) => props.theme.tokens.radius.xs}px;
	font-weight: 600;
`;

export default function FeedbackRequestExternalConfirmModal({
	externals,
	internalCount,
	isSubmitting = false,
	onAdjust,
	onConfirm,
}: IProps) {
	const { t } = useTranslation();
	const theme = useTheme();

	return (
		<Popover center backdrop visible onDismiss={onAdjust}>
			<Modal width="400px">
				<Modal.Header
					title={
						<Flex alignItems="center">
							<WarningColorIcon width={24} color={theme.tokens.warning.icon} />
							<Box ml="4px">{t('feedback.request.externalconfirm.title')}</Box>
						</Flex>
					}
				/>
				<Modal.Contents>
					<Paragraph mb={2}>
						<Trans
							i18nKey="feedback.request.externalconfirm.description"
							count={externals.length}
							components={{ strong: <strong /> }}
						/>
					</Paragraph>
					<Flex flexDirection="column">
						{externals.map((recipient, index) => (
							<RecipientCard key={`${recipient.email}-${index}`} px={3} py={2} mb={2}>
								<Paragraph>{recipient.name}</Paragraph>
								<Box mt="2px">
									<Paragraph>
										<EmailHighlight>{recipient.email}</EmailHighlight>
									</Paragraph>
								</Box>
							</RecipientCard>
						))}
					</Flex>
					{internalCount > 0 && (
						<Paragraph mt={4}>
							{t('feedback.request.externalconfirm.internalnote', { count: internalCount })}
						</Paragraph>
					)}
				</Modal.Contents>
				<Modal.Footer>
					<Button
						type="secondary"
						title={t('feedback.request.externalconfirm.adjust')}
						onClick={onAdjust}
						disabled={isSubmitting}
					/>
					<Button
						type="primary"
						title={t('feedback.request.externalconfirm.confirm')}
						onClick={onConfirm}
						loading={isSubmitting}
						disabled={isSubmitting}
						ml={2}
					/>
				</Modal.Footer>
			</Modal>
		</Popover>
	);
}
